import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined,
  RollbackOutlined, GlobalOutlined, SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Modal, message as antMessage, Input, Tabs, Tag, Empty, Spin, Row, Col, Pagination, Select } from 'antd';
import { botFlowApi } from './services/botFlowApi';
import AiFlowGenerator from './AiFlowGenerator';
import './FlowList.css';

const getBotApiStatusCode = (res) => Number(res?.data?.statuscode ?? res?.data?.statusCode);

const assertBotApiSuccess = (res) => {
  const code = getBotApiStatusCode(res);
  const failed = (Number.isFinite(code) && code >= 400) || res?.data?.status === false;
  if (!failed) return res;
  const error = new Error(res?.data?.message || 'Request failed');
  error.response = { data: res?.data };
  throw error;
};

const unwrapBotResponse = (res) => {
  assertBotApiSuccess(res);
  return res.data?.entity || res.data?.payload || res.data?.data || res.data;
};

const getBotApiErrorMessage = (error, fallback = 'Request failed') => (
  error?.response?.data?.message
  || error?.data?.message
  || error?.message
  || fallback
);

const createJsonPlaceholderApiDemo = async () => {
  const flow = unwrapBotResponse(await botFlowApi.createFlow({
    name: 'JSONPlaceholder Users API Demo',
    description: 'Demo flow that calls a public users API and maps response fields into bot variables.',
    trigger_type: 'always',
  }));

  const flowId = flow.id;
  const createdNodes = [];
  const createNode = async (body) => {
    const node = unwrapBotResponse(await botFlowApi.createNode(flowId, body));
    createdNodes.push(node);
    return node;
  };

  try {
    const startNode = await createNode({
      node_type: 'message',
      is_start: true,
      position: { x: 80, y: 180 },
      content: {
        text: 'Tap Get Data and I will fetch sample user details from the public JSONPlaceholder API.',
        options: [{ id: 'get_data', label: 'Get Data' }],
        buttons: [{
          id: 'get_data',
          payload: 'get_data',
          value: 'get_data',
          label: 'Get Data',
          text: 'Get Data',
          title: 'Get Data',
        }],
      },
    });

    const apiNode = await createNode({
      node_type: 'api_call',
      is_start: false,
      position: { x: 430, y: 180 },
      content: {
        url: 'https://jsonplaceholder.typicode.com/users',
        method: 'GET',
        headers: {},
        response_map: {
          first_user_name: '0.name',
          first_user_email: '0.email',
          first_user_phone: '0.phone',
          first_user_city: '0.address.city',
          first_company: '0.company.name',
        },
        success_text: '',
        error_text: "Sorry, I couldn't fetch the sample users right now.",
        timeout_ms: 8000,
      },
    });

    const successNode = await createNode({
      node_type: 'message',
      is_start: false,
      position: { x: 800, y: 90 },
      content: {
        text: 'Here is the first user from the API:\nName: {{first_user_name}}\nEmail: {{first_user_email}}\nPhone: {{first_user_phone}}\nCity: {{first_user_city}}\nCompany: {{first_company}}',
      },
    });

    const errorNode = await createNode({
      node_type: 'message',
      is_start: false,
      position: { x: 800, y: 300 },
      content: {
        text: "I couldn't fetch the users API right now. Please try again later.",
      },
    });

    const endNode = await createNode({
      node_type: 'end',
      is_start: false,
      position: { x: 1160, y: 190 },
      content: { text: 'Done. Thanks!' },
    });

    await Promise.all([
      botFlowApi.createEdge(flowId, { from_node_id: startNode.id, condition: 'get_data', to_node_id: apiNode.id }).then(assertBotApiSuccess),
      botFlowApi.createEdge(flowId, { from_node_id: apiNode.id, condition: 'success', to_node_id: successNode.id }).then(assertBotApiSuccess),
      botFlowApi.createEdge(flowId, { from_node_id: apiNode.id, condition: 'error', to_node_id: errorNode.id }).then(assertBotApiSuccess),
      botFlowApi.createEdge(flowId, { from_node_id: successNode.id, condition: null, to_node_id: endNode.id }).then(assertBotApiSuccess),
      botFlowApi.createEdge(flowId, { from_node_id: errorNode.id, condition: null, to_node_id: endNode.id }).then(assertBotApiSuccess),
    ]);

    return flow;
  } catch (error) {
    await botFlowApi.deleteFlow(flowId).catch(() => {});
    throw error;
  }
};


const FlowList = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  // My Flows search
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('my-flows');
  // Flow Library state
  const [libTemplates, setLibTemplates]     = useState([]);
  const [libLoading, setLibLoading]         = useState(false);
  const [libSearch, setLibSearch]           = useState('');
  const [libCategory, setLibCategory]       = useState('');
  const [libPage, setLibPage]               = useState(1);
  const [libTotal, setLibTotal]             = useState(0);
  const [libCategories, setLibCategories]   = useState([]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const flowsRes = await botFlowApi.listFlows();
      const list = unwrapBotResponse(flowsRes) || [];
      setFlows(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load flows');
    } finally {
      setLoading(false);
    }
  };

  const loadLibrary = async (page, srch, category) => {
    const p   = page     ?? libPage;
    const s   = srch     ?? libSearch;
    const cat = category ?? libCategory;
    setLibLoading(true);
    try {
      const params = { page: p, limit: 12 };
      if (s.trim())  params.search   = s.trim();
      if (cat)       params.category = cat;
      const res     = await botFlowApi.listTemplates(params);

      // Read total BEFORE unwrapping — unwrapBotResponse strips the envelope.
      // Response shape: { entity: { templates: [...], pagination: { total, pages } } }
      const payload = unwrapBotResponse(res);
      const list    = Array.isArray(payload)
        ? payload
        : (payload?.templates || payload?.items || payload?.data || payload || []);

      const total =
        payload?.pagination?.total ?? payload?.meta?.total ??
        res.data?.total ?? res.data?.count ??
        list.length;

      setLibTemplates(Array.isArray(list) ? list : []);
      setLibTotal(typeof total === 'number' ? total : (Array.isArray(list) ? list.length : 0));
    } catch (e) {
      antMessage.error(getBotApiErrorMessage(e, 'Failed to load template library'));
    } finally {
      setLibLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  // Load library when the tab is first activated, or when page/category changes.
  useEffect(() => {
    if (activeTab !== 'library') return;
    if (libCategories.length === 0) {
      botFlowApi.listTemplateCategories()
        .then((res) => {
          const cats = res.data?.categories || res.data?.entity?.categories || [];
          setLibCategories(Array.isArray(cats) ? cats : []);
        })
        .catch(() => {});
    }
    loadLibrary(libPage, libSearch, libCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, libPage, libCategory]);

  const handleCreate = () => {
    let nameValue = 'My Flow';
    Modal.confirm({
      title: 'New flow name',
      content: <Input defaultValue={nameValue} onChange={(e) => { nameValue = e.target.value; }} placeholder="e.g. Support Bot" autoFocus />,
      okText: 'Create',
      cancelText: 'Cancel',
      onOk: async () => {
        const name = (nameValue || '').trim();
        if (!name) return;
        try {
          const res = await botFlowApi.createFlow({ name, description: '', trigger_type: 'always' });
          const flow = unwrapBotResponse(res);
          if (flow?.id) navigate(`/dashboard/bot-flow/${flow.id}`);
          else load();
        } catch (e) {
          antMessage.error(getBotApiErrorMessage(e, 'Create failed'));
        }
      },
    });
  };

  const handleCreateApiDemo = () => {
    Modal.confirm({
      title: 'Create API Demo Flow',
      content: 'Create a sample flow that calls https://jsonplaceholder.typicode.com/users and maps the first user into message variables?',
      okText: 'Create',
      cancelText: 'Cancel',
      onOk: async () => {
        setBusy(true);
        try {
          const flow = await createJsonPlaceholderApiDemo();
          if (flow?.id) navigate(`/dashboard/bot-flow/${flow.id}`);
          else await load();
        } catch (error) {
          const msg = getBotApiErrorMessage(error, 'Could not create the sample API flow');
          if (msg.includes('node_type') && msg.includes('ai_handoff')) {
            antMessage.error(`Backend still does not accept api_call nodes. Please deploy the backend api_call update first. Backend said: ${msg}`);
          } else {
            antMessage.error(msg);
          }
          await load();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleClone = (tpl) => {
    let cloneName = `${tpl.name} (copy)`;
    let activateNow = false;
    Modal.confirm({
      title: `Clone "${tpl.name}"`,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          <Input defaultValue={cloneName} onChange={(e) => { cloneName = e.target.value; }} placeholder="New flow name" autoFocus />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" onChange={(e) => { activateNow = e.target.checked; }} />
            Activate this flow immediately
          </label>
        </div>
      ),
      okText: 'Clone',
      cancelText: 'Cancel',
      onOk: async () => {
        setBusy(true);
        try {
          await botFlowApi.cloneTemplate(tpl.slug, { name: cloneName || undefined, activate: activateNow }).then(assertBotApiSuccess);
          await load();
        } catch (e) {
          antMessage.error(getBotApiErrorMessage(e, 'Clone failed'));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleReset = (flow) => {
    Modal.confirm({
      title: 'Reset to default template',
      content: `Reset "${flow.name}" to its default template? All edits will be lost.`,
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setBusy(true);
        try {
          await botFlowApi.resetToDefault(flow.id).then(assertBotApiSuccess);
          await load();
        } catch (e) {
          antMessage.error(getBotApiErrorMessage(e, 'Reset failed'));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const toggleActivate = async (flow) => {
    try {
      if (flow.is_active) await botFlowApi.deactivate(flow.id).then(assertBotApiSuccess);
      else await botFlowApi.activate(flow.id).then(assertBotApiSuccess);
      load();
    } catch (e) {
      antMessage.error(getBotApiErrorMessage(e, 'Action failed'));
    }
  };

  const handleDelete = (flow) => {
    Modal.confirm({
      title: 'Delete flow',
      content: `Delete flow "${flow.name}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await botFlowApi.deleteFlow(flow.id).then(assertBotApiSuccess);
          load();
        } catch (e) {
          antMessage.error(getBotApiErrorMessage(e, 'Delete failed'));
        }
      },
    });
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const filteredFlows = useMemo(() => {
    if (!search.trim()) return flows;
    const q = search.toLowerCase();
    return flows.filter((f) =>
      f.name.toLowerCase().includes(q) ||
      (f.description || '').toLowerCase().includes(q),
    );
  }, [flows, search]);

  const activeCount = useMemo(() => flows.filter((f) => f.is_active).length, [flows]);

  // ── Loading / error ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fl-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="fl-error">
        <span>&#9888; {err}</span>
        <button className="btn btn-secondary" onClick={load} style={{ marginLeft: 8 }}>Retry</button>
      </div>
    );
  }

  // ── My Flows tab ─────────────────────────────────────────────────────────────
  const myFlowsTab = (
    <div>
      {flows.length > 0 && (
        <div className="fl-section-bar">
          <Input
            className="fl-search"
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Search flows..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          <span className="fl-count">
            {filteredFlows.length} of {flows.length} flow{flows.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {flows.length === 0 && (
        <div className="fl-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: '#64748b', fontSize: 14 }}>
                No flows yet. Create one from scratch or clone from the{' '}
                <span
                  style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => setActiveTab('library')}
                >
                  Flow Library
                </span>.
              </span>
            }
          >
            <button className="btn btn-primary" onClick={handleCreate} disabled={busy}>
              <PlusOutlined /> Create your first flow
            </button>
          </Empty>
        </div>
      )}

      {flows.length > 0 && filteredFlows.length === 0 && (
        <div className="fl-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: '#64748b' }}>No flows match &ldquo;{search}&rdquo;</span>}
          />
        </div>
      )}

      {filteredFlows.map((flow) => (
        <div key={flow.id} className={`fl-card${flow.is_active ? ' fl-card--active' : ''}`}>
          <div className="fl-card-info">
            <div className="fl-card-title-row">
              <span className="fl-card-name">{flow.name}</span>
              {flow.is_active && (
                <Tag color="success" style={{ borderRadius: 8, fontWeight: 600, fontSize: 11, lineHeight: '18px' }}>ACTIVE</Tag>
              )}
              {flow.template_slug && (
                flow.is_template_locked
                  ? <Tag color="blue" style={{ borderRadius: 8, fontWeight: 600, fontSize: 11, lineHeight: '18px' }}>DEFAULT</Tag>
                  : <Tag color="gold" style={{ borderRadius: 8, fontWeight: 600, fontSize: 11, lineHeight: '18px' }}>CUSTOMISED</Tag>
              )}
            </div>
            <p className="fl-card-desc">
              <span>{flow.description || 'No description'}</span>
              <span className="fl-card-desc-dot">&middot;</span>
              <span>trigger: <span className="fl-card-code">{flow.trigger_type}</span></span>
              {flow.template_slug && (
                <>
                  <span className="fl-card-desc-dot">&middot;</span>
                  <span>template: <span className="fl-card-code">{flow.template_slug}</span></span>
                </>
              )}
            </p>
          </div>
          <div className="fl-card-actions">
            <button
              className="btn btn-secondary"
              onClick={() => toggleActivate(flow)}
              disabled={busy}
            >
              <PoweroffOutlined /> {flow.is_active ? 'Deactivate' : 'Activate'}
            </button>
            {flow.template_slug && (
              <button
                className="btn btn-secondary"
                onClick={() => handleReset(flow)}
                disabled={busy}
                title="Wipe nodes/edges and re-clone the bound template"
              >
                <RollbackOutlined /> Reset
              </button>
            )}
            <Link className="btn btn-primary" to={`/dashboard/bot-flow/${flow.id}`}>
              <EditOutlined /> Edit
            </Link>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(flow)}
              disabled={busy || flow.is_active}
              title={flow.is_active ? 'Deactivate before deleting' : 'Delete flow'}
              style={{ padding: '6px 10px' }}
            >
              <DeleteOutlined />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // ── Flow Library tab ──────────────────────────────────────────────────────────
  const getBadgeStyle = (label) => {
    if (!label) return { bg: '#4f46e5', color: '#fff' };
    const l = label.toLowerCase();
    if (l.includes('high impact') || l.includes('popular')) return { bg: '#0f172a', color: '#fff' };
    if (l.includes('support'))  return { bg: '#7c3aed', color: '#fff' };
    if (l.includes('new'))      return { bg: '#65a30d', color: '#fff' };
    if (l.includes('useful'))   return { bg: '#0369a1', color: '#fff' };
    if (l.includes('trending')) return { bg: '#c2410c', color: '#fff' };
    return { bg: '#4f46e5', color: '#fff' };
  };

  const handleLibSearch = () => {
    setLibPage(1);
    loadLibrary(1, libSearch, libCategory);
  };

  const libraryTab = (
    <div>
      {/* Search + filter row */}
      <div className="fl-lib-toolbar">
        <Input.Search
          className="fl-search"
          placeholder="Search templates..."
          value={libSearch}
          onChange={(e) => {
            setLibSearch(e.target.value);
            if (!e.target.value) { setLibPage(1); loadLibrary(1, '', libCategory); }
          }}
          onSearch={handleLibSearch}
          allowClear
          style={{ flex: '1 1 220px', maxWidth: 320 }}
        />
        <Select
          className="fl-cat-select"
          placeholder="All Categories"
          allowClear
          value={libCategory || undefined}
          onChange={(val) => {
            const cat = val || '';
            setLibCategory(cat);
            setLibPage(1);
          }}
          style={{ flex: '0 0 220px', minWidth: 180 }}
          options={[
            ...libCategories.map((c) => ({ value: c, label: c })),
          ]}
        />
      </div>

      {libLoading ? (
        <div className="fl-loading" style={{ minHeight: 220 }}>
          <Spin size="large" />
        </div>
      ) : libTemplates.length === 0 ? (
        <div className="fl-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: '#64748b' }}>
                No templates found{libSearch || libCategory ? ' for these filters' : ''}.
              </span>
            }
          />
        </div>
      ) : (
        <>
          <Row gutter={[20, 20]}>
            {libTemplates.map((item) => {
              const impact      = item.impact_percentage ?? item.impact ?? item.metric;
              const impactLabel = item.impact_label ?? item.short_description ?? item.subtitle ?? '';
              const badgeLabel  = item.badge_label ?? item.badge ?? '';
              const badgeStyle  = getBadgeStyle(badgeLabel);
              const tags        = Array.isArray(item.tags) ? item.tags : [];
              const features    = Array.isArray(item.features) ? item.features : [];
              return (
                <Col key={item.slug} xs={24} sm={12} lg={8}>
                  <div className="fl-tpl-card">
                    {/* Name + badge */}
                    <div className="fl-tpl-header">
                      <span className="fl-tpl-name">{item.name}</span>
                      {badgeLabel && (
                        <span
                          className="fl-tpl-badge"
                          style={{ background: badgeStyle.bg, color: badgeStyle.color }}
                        >
                          {badgeLabel}
                        </span>
                      )}
                    </div>

                    {/* Subtitle */}
                    {(item.short_description || item.subtitle) && (
                      <p className="fl-tpl-subtitle">{item.short_description || item.subtitle}</p>
                    )}

                    {/* Impact metric */}
                    {impact != null && (
                      <div className="fl-tpl-metric">
                        <span className="fl-tpl-metric-pct">{impact}%</span>
                        <span className="fl-tpl-metric-arrow">&#8599;</span>
                        {impactLabel && <span className="fl-tpl-metric-label">{impactLabel}</span>}
                      </div>
                    )}

                    {/* Description */}
                    <p className="fl-tpl-desc">{item.description || 'No description.'}</p>

                    {/* Tags */}
                    {tags.length > 0 && (
                      <div className="fl-tpl-tags">
                        {tags.map((t) => <span key={t} className="fl-tpl-tag">{t}</span>)}
                      </div>
                    )}

                    <div style={{ flex: 1 }} />

                    {/* Features */}
                    {features.length > 0 && (
                      <>
                        <div className="fl-tpl-divider" />
                        <div className="fl-tpl-features">
                          {features.slice(0, 3).map((f, idx) => (
                            <div key={idx} className="fl-tpl-feature">
                              <span className="fl-tpl-feature-icon">{f.icon || '\u2699\uFE0F'}</span>
                              <span className="fl-tpl-feature-label">{f.label || f.name || ''}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Primary CTA */}
                    <button
                      type="button"
                      className="fl-tpl-cta"
                      onClick={() => handleClone(item)}
                      disabled={busy}
                    >
                      Use this Template
                    </button>
                  </div>
                </Col>
              );
            })}
          </Row>

          {(libTotal > libPage * 12 || libPage > 1) && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
              <Pagination
                current={libPage}
                pageSize={12}
                total={libTotal}
                onChange={(page) => setLibPage(page)}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Top toolbar */}
      <div className="fl-toolbar">
        <div className="fl-toolbar-meta">
          <span className="fl-count">{flows.length} flow{flows.length !== 1 ? 's' : ''} configured</span>
          {activeCount > 0 && (
            <span className="fl-active-pill">
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
              {activeCount} active
            </span>
          )}
        </div>
        <div className="fl-toolbar-actions">
          <button className="btn btn-secondary" onClick={handleCreateApiDemo} disabled={busy}>
            <GlobalOutlined /> Sample API Users Flow
          </button>
          <button className="btn btn-ai" onClick={() => setShowAiGenerator(true)} disabled={busy}>
            <ThunderboltOutlined /> Generate with AI
          </button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={busy}>
            <PlusOutlined /> New Flow
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="fl-tabs"
        items={[
          {
            key: 'my-flows',
            label: (
              <span>
                My Flows
                {flows.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6, fontSize: 11,
                      background: activeTab === 'my-flows' ? '#ede9fe' : '#f1f5f9',
                      color: activeTab === 'my-flows' ? '#5b21b6' : '#64748b',
                      padding: '1px 7px', borderRadius: 9, fontWeight: 600,
                    }}
                  >
                    {flows.length}
                  </span>
                )}
              </span>
            ),
            children: myFlowsTab,
          },
          {
            key: 'library',
            label: (
              <span>
                Flow Library
                {libTotal > 0 && (
                  <span
                    style={{
                      marginLeft: 6, fontSize: 11,
                      background: activeTab === 'library' ? '#ede9fe' : '#f1f5f9',
                      color: activeTab === 'library' ? '#5b21b6' : '#64748b',
                      padding: '1px 7px', borderRadius: 9, fontWeight: 600,
                    }}
                  >
                    {libTotal}
                  </span>
                )}
              </span>
            ),
            children: libraryTab,
          },
        ]}
      />

      <AiFlowGenerator
        open={showAiGenerator}
        onClose={() => { setShowAiGenerator(false); load(); }}
      />
    </>
  );
};

export default FlowList;
